import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { ArrowDown20Filled, ArrowSort20Regular } from "@fluentui/react-icons";
import { Tooltip } from "../components/Tooltip";
import type { InvoiceTotals } from "./faturasApi";

/**
 * Filtros por coluna ao estilo do Excel: uma segunda linha no `<thead>` com um controlo por baixo
 * de cada título. É o que o utilizador espera de quem trabalha a folha de cálculo — filtrar na
 * coluna e ver o total acompanhar.
 *
 * A linha está sempre visível de propósito: o total do rodapé responde aos filtros, e escondê-los
 * atrás de um botão deixava quem chega ao ecrã a olhar para uma soma sem saber o que a restringe.
 *
 * Só se usam classes que já existem no `index.css` — é output do Tailwind compilado e commitado,
 * não há build step, por isso uma classe inventada não produz estilo nenhum em silêncio.
 */

// Sem filtro o controlo é quase invisível — só o texto e a setinha — para a linha não competir
// com os dados. Com filtro ganha moldura e cor, porque é o que explica o total do rodapé: quem
// olha para a soma tem de perceber logo qual coluna a está a restringir.
const CONTROL_BASE =
  "w-full px-2 py-1 text-xs border focus:outline-none focus:ring-2 focus:ring-primary/20";

const CONTROL_IDLE =
  "border-transparent bg-transparent text-secondary-text hover:border-border";

const CONTROL_ACTIVE = "border-primary bg-background text-primary font-medium";

// A largura mínima é o que impede as colunas de colapsarem: num <table> com layout automático
// um controlo `w-full` não conta para a largura intrínseca da coluna, que encolhia até ao
// tamanho do título e cortava o rótulo do filtro ("Todas" → "Tod").
const CONTROL_STYLE = {
  borderRadius: "var(--radius-button)",
  minWidth: "6.5rem",
} as const;

const controlClass = (isActive: boolean): string =>
  `${CONTROL_BASE} ${isActive ? CONTROL_ACTIVE : CONTROL_IDLE}`;

export interface ColumnFilterOption {
  value: string;
  label: string;
}

/** Célula de filtro com um dropdown. `value` vazio = sem filtro nesta coluna. */
export function ColumnFilterSelect({
  value,
  onChange,
  allLabel,
  options,
  title,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  allLabel: string;
  options: ColumnFilterOption[];
  title?: string;
  /**
   * Nome da coluna, para quem não a vê. O cabeçalho vive noutra linha da tabela e não há ligação
   * programática entre os dois: sem isto, um leitor de ecrã anuncia só "combobox" e o utilizador
   * não sabe qual das seis colunas está a filtrar. O `title` não serve — vai para a dica do rato,
   * é descrição e não nome, e desaparece ao sair o foco.
   */
  label?: string;
}) {
  return (
    <th className="px-3 pb-2 font-normal align-top">
      <Tooltip text={title}>
        <select
          aria-label={label}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={controlClass(Boolean(value))}
          style={CONTROL_STYLE}
        >
          <option value="">{allLabel}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </Tooltip>
    </th>
  );
}

/** Célula de filtro com caixa de texto (contém). */
export function ColumnFilterText({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <th className="px-3 pb-2 font-normal align-top">
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={controlClass(Boolean(value))}
        style={CONTROL_STYLE}
      />
    </th>
  );
}

/** Célula de filtro vazia, para colunas sem filtro (seleção, Ações). */
export function ColumnFilterEmpty() {
  return <th className="px-3 pb-2" />;
}

/**
 * Popover de um filtro de intervalo (datas ou valores). O botão na linha de filtros mostra o
 * intervalo ativo por extenso — o popover só guarda os inputs, para a célula não rebentar a
 * largura da coluna com dois campos lado a lado.
 *
 * O painel é desenhado num portal no `body` com `position: fixed` (o mesmo padrão do Tooltip) —
 * dentro do `overflow` da grelha, um painel `absolute` era cortado mal a tabela tivesse poucas
 * linhas. Com scroll/resize a âncora move-se e o painel não; fechar é o comportamento honesto.
 */
function ColumnFilterPopover({
  label,
  isActive,
  title,
  align,
  children,
  onClear,
}: {
  label: string;
  isActive: boolean;
  title?: string;
  /** `right` nas colunas encostadas à direita, para o painel abrir para dentro do ecrã. */
  align: "left" | "right";
  children: ReactNode;
  onClear: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(
    null,
  );

  // O painel renderiza primeiro invisível para se poder medir; só depois é posicionado.
  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    const trigger = triggerRef.current?.getBoundingClientRect();
    const panel = panelRef.current?.getBoundingClientRect();
    if (!trigger || !panel) return;

    const top = Math.min(trigger.bottom + 4, window.innerHeight - panel.height - 8);
    const left = Math.max(
      8,
      Math.min(
        align === "right" ? trigger.right - panel.width : trigger.left,
        window.innerWidth - panel.width - 8,
      ),
    );
    setPosition({ top, left });
  }, [isOpen, align]);

  // Fecha ao clicar fora — o gatilho está na tabela e o painel no portal, os dois contam.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const close = (event: Event) => {
      // Scroll dentro do próprio painel não conta como "a âncora fugiu".
      if (
        event.target instanceof Node &&
        panelRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsOpen(false);
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [isOpen]);

  return (
    <th className="px-3 pb-2 font-normal align-top">
      <Tooltip text={title}>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setIsOpen((previous) => !previous)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setIsOpen(false);
          }}
          className={`${controlClass(isActive)} text-left`}
          style={{ ...CONTROL_STYLE, whiteSpace: "nowrap" }}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        >
          {label}
        </button>
      </Tooltip>
      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            className="bg-card border border-border shadow-lg p-3 flex flex-col gap-2"
            onKeyDown={(event) => {
              if (event.key === "Escape") setIsOpen(false);
            }}
            style={{
              position: "fixed",
              zIndex: 60,
              minWidth: "13rem",
              borderRadius: "var(--radius-card)",
              ...(position
                ? { top: position.top, left: position.left }
                : { top: 0, left: 0, visibility: "hidden" as const }),
            }}
          >
            {children}
            {isActive && (
              <button
                type="button"
                onClick={() => {
                  onClear();
                  setIsOpen(false);
                }}
                className="px-2 py-1 text-xs border border-border bg-background text-foreground hover:bg-accent/30"
                style={CONTROL_STYLE}
              >
                Limpar
              </button>
            )}
          </div>,
          document.body,
        )}
    </th>
  );
}

const RANGE_INPUT_CLASS =
  "px-2 py-1 text-xs border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20";

/** `2026-08-05` (input date) → `05/08/2026`, como o resto da grelha. */
const formatIsoDate = (iso: string): string => {
  const [year, month, day] = iso.split("-");
  return `${day}/${month}/${year}`;
};

/** Ano a dois dígitos (`05/08/26`) — com as duas pontas do intervalo no botão, o formato
 * completo partia o rótulo em duas linhas. */
const formatIsoDateShort = (iso: string): string => {
  const [year, month, day] = iso.split("-");
  return `${day}/${month}/${year.slice(2)}`;
};

/** Célula de filtro com intervalo de datas (De/Até), inclusivo nas duas pontas. */
export function ColumnFilterDateRange({
  from,
  to,
  onChange,
  title,
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  title?: string;
}) {
  const isActive = Boolean(from || to);
  const label = !isActive
    ? "Todas"
    : from && to
      ? `${formatIsoDateShort(from)} – ${formatIsoDateShort(to)}`
      : from
        ? `desde ${formatIsoDate(from)}`
        : `até ${formatIsoDate(to)}`;

  // O intervalo nunca fica invertido: mover uma ponta para lá da outra arrasta a outra consigo
  // (datas ISO comparam-se como texto). Os min/max nos inputs bloqueiam os dias inválidos no
  // picker; isto cobre o que for escrito à mão.
  const changeFrom = (value: string) =>
    onChange(value, to && value && value > to ? value : to);
  const changeTo = (value: string) =>
    onChange(from && value && value < from ? value : from, value);

  return (
    <ColumnFilterPopover
      label={label}
      isActive={isActive}
      title={title}
      align="left"
      onClear={() => onChange("", "")}
    >
      <label className="flex items-center justify-between gap-2 text-xs text-secondary-text">
        De
        <input
          type="date"
          value={from}
          max={to || undefined}
          onChange={(event) => changeFrom(event.target.value)}
          className={RANGE_INPUT_CLASS}
          style={CONTROL_STYLE}
        />
      </label>
      <label className="flex items-center justify-between gap-2 text-xs text-secondary-text">
        Até
        <input
          type="date"
          value={to}
          min={from || undefined}
          onChange={(event) => changeTo(event.target.value)}
          className={RANGE_INPUT_CLASS}
          style={CONTROL_STYLE}
        />
      </label>
    </ColumnFilterPopover>
  );
}

/**
 * Texto escrito → número, ou null enquanto não for um número válido (mandar NaN na query string
 * dava 400 no binder). Aceita as formas em que o valor aparece à frente de quem procura: vírgula
 * decimal, o ponto dos milhares como a grelha o escreve ("1.234,56"), ponto decimal e o símbolo
 * do euro. É a mesma leitura que o servidor faz da caixa de pesquisa.
 */
export const toAmount = (raw: string): number | null => {
  const cleaned = raw.replace(/[€\s]/g, "");
  if (!cleaned) return null;

  const normalized = cleaned.includes(",")
    ? cleaned.replace(/\./g, "").replace(",", ".")
    : /^-?\d{1,3}(\.\d{3})+$/.test(cleaned)
      ? cleaned.replace(/\./g, "")
      : cleaned;

  const value = Number(normalized);
  return Number.isNaN(value) ? null : value;
};

/**
 * Célula de filtro por valor — um único campo, o valor que se procura. Havia mínimo e máximo, e o
 * cliente pediu para ficar só um: quem procura uma fatura tem o valor à frente e escreve-o.
 * O valor é a string tal como sai do input; a conversão para número é de quem consome.
 */
export function ColumnFilterAmount({
  value,
  onChange,
  title,
}: {
  value: string;
  onChange: (value: string) => void;
  title?: string;
}) {
  const isActive = Boolean(value);

  return (
    <ColumnFilterPopover
      label={isActive ? value : "Todos"}
      isActive={isActive}
      title={title}
      align="right"
      onClear={() => onChange("")}
    >
      <label className="flex items-center justify-between gap-2 text-xs text-secondary-text">
        Valor
        <input
          type="text"
          inputMode="decimal"
          placeholder="1234,56"
          value={value}
          // Só o que pode fazer parte de um valor: letras deixariam o filtro marcado como activo
          // sem filtrar nada, porque o servidor não recebe número nenhum. Vive aqui e só aqui —
          // num selector, o valor sai das nossas próprias opções e não há nada a limpar.
          onChange={(event) =>
            onChange(event.target.value.replace(/[^\d.,\s€-]/g, ""))
          }
          className={RANGE_INPUT_CLASS}
          style={{ ...CONTROL_STYLE, width: "6.5rem" }}
        />
      </label>
    </ColumnFilterPopover>
  );
}

/**
 * Rótulo da linha de totais. Diz explicitamente sobre quantas faturas é a soma, porque o total
 * cobre o conjunto filtrado inteiro e não apenas as linhas desenhadas na grelha.
 */
export function totalsLabel(totals: InvoiceTotals): string {
  if (totals.count === 0) return "Sem faturas no filtro";
  if (totals.count === 1) return "Total de 1 fatura";
  return `Total de ${totals.count} faturas`;
}

/**
 * Data do input (`2026-08-19`) → instante do início desse dia **no fuso de quem está a ver**
 * (`2026-08-19T00:00:00+01:00`), ou null enquanto não houver data.
 *
 * Uma data nua não leva fuso nenhum, e o servidor tem de lhe atribuir um: o .NET completa-a com o
 * fuso da máquina onde o processo corre — em Azure, tipicamente UTC, que não tem nada a ver com o
 * dia de quem faz a pergunta. Como a data de entrada é um instante verdadeiro e não uma data
 * impressa, essa diferença desloca o limite do dia e faz o que entrou ao fim da noite cair no dia
 * errado. Mandando o fuso explícito, o dia é o de quem está a olhar para o ecrã.
 */
export const toLocalDayIso = (day: string): string | null => {
  if (!day) return null;

  const [year, month, dayOfMonth] = day.split("-").map(Number);
  if (!year || !month || !dayOfMonth) return null;

  const minutesFromUtc = -new Date(year, month - 1, dayOfMonth).getTimezoneOffset();
  const sign = minutesFromUtc < 0 ? "-" : "+";
  const absolute = Math.abs(minutesFromUtc);
  const hours = String(Math.floor(absolute / 60)).padStart(2, "0");
  const minutes = String(absolute % 60).padStart(2, "0");

  return `${day}T00:00:00${sign}${hours}:${minutes}`;
};

/**
 * Texto que existe só para quem usa leitor de ecrã. Em estilo inline porque a classe `sr-only` não
 * existe no index.css compilado deste projeto — escrevê-la não daria erro nenhum e o texto ficaria
 * simplesmente visível no meio da tabela.
 */
const APENAS_LEITOR_DE_ECRA: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  borderWidth: 0,
};

export function SrOnly({ children }: { children: ReactNode }) {
  return <span style={APENAS_LEITOR_DE_ECRA}>{children}</span>;
}

/**
 * Cabeçalho de coluna que ordena a lista. A seta está sempre à vista nas colunas ordenáveis — é
 * assim que se sabe que se pode clicar nelas sem ter de as descobrir por acaso.
 *
 * O estado não se distingue por cor: a coluna activa leva uma seta <b>cheia</b> que aponta para o
 * sentido da ordenação, e as inactivas a seta dupla de "ordenável". Só pela cor, quem não a
 * distingue não teria como saber qual das colunas manda — e no caso mais comum, descendente, a
 * seta activa e a inactiva apontavam para o mesmo lado.
 *
 * Cores dos tokens e não opacidade: esbater com opacidade depende do fundo e ficava a 3,08:1, em
 * cima do mínimo. Cor, transição e o `sr-only` vão em estilo inline pela razão de sempre — o
 * index.css é uma folha compilada e há classes que não existem lá (`invisible` é uma delas, e foi
 * por isso que as duas setas apareceram ao mesmo tempo).
 */
export function SortHeader({
  label,
  active,
  ascending,
  title,
  onClick,
}: {
  label: string;
  active: boolean;
  ascending: boolean;
  title: string;
  onClick: () => void;
}) {
  return (
    <th
      className="px-3 py-2 font-medium"
      aria-sort={!active ? "none" : ascending ? "ascending" : "descending"}
    >
      <button
        type="button"
        title={title}
        onClick={onClick}
        className="inline-flex items-center gap-1 font-medium"
      >
        {label}
        {active ? (
          <ArrowDown20Filled
            className="w-4 h-4"
            style={{
              color: "var(--foreground)",
              transform: ascending ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 150ms ease",
            }}
          />
        ) : (
          <ArrowSort20Regular
            className="w-4 h-4"
            style={{ color: "var(--secondary-text)" }}
          />
        )}
      </button>
    </th>
  );
}
